import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ShieldCheck, XCircle, Building2 } from "lucide-react";
import { motion } from "framer-motion";
import { api, fileUrl } from "@/lib/api";

function formatDate(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("ms-MY", { day: "2-digit", month: "long", year: "numeric" });
}

export default function MemberPublicProfile() {
  const { memberNumber } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/public/members/${memberNumber}`)
      .then((r) => setData(r.data))
      .catch(() => setError("Profil ahli tidak dijumpai"))
      .finally(() => setLoading(false));
  }, [memberNumber]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-piks-muted">Memuatkan profil...</div>;

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-piks-ivory px-4" data-testid="public-profile-error">
        <div className="text-center">
          <XCircle className="w-12 h-12 text-piks-red mx-auto mb-3" />
          <p className="font-semibold text-piks-black">{error}</p>
          <Link to="/semak" className="text-sm text-piks-red underline mt-2 inline-block">Kembali ke Semak Ahli</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-piks-soft-neutral py-12 px-4 pt-28" data-testid="public-profile-page">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <p className="section-eyebrow justify-center">BODYGUARD SARAWAK / VERIFIED MEMBER</p>
        </div>
        <div className="relative bg-piks-black rounded-2xl p-8 text-center text-white mb-6 overflow-hidden">
          <div className="absolute top-0 right-0 w-1.5 h-full bg-piks-red" />
          <div className="w-24 h-24 rounded-full bg-white/10 border-2 border-piks-gold mx-auto mb-4 overflow-hidden flex items-center justify-center">
            {(data.passport_photo_file_id || data.profile_photo_file_id) ? (
              <img src={fileUrl(data.passport_photo_file_id || data.profile_photo_file_id)} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="font-display text-3xl">{data.full_name?.[0]}</span>
            )}
          </div>
          <p className="font-display text-2xl font-extrabold mb-1" data-testid="public-profile-name">{data.full_name}</p>
          <p className="font-mono text-piks-gold text-sm">{data.member_number}</p>
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            {data.badges.map((b) => (
              <span key={b} className="text-[10px] font-semibold tracking-wide px-2.5 py-1 rounded-full bg-piks-gold/20 text-white flex items-center gap-1">
                <ShieldCheck className="w-3 h-3 text-piks-gold" /> {b}
              </span>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-piks-border p-7 space-y-3">
          <div className="flex justify-between py-2.5 border-b border-piks-border"><span className="text-piks-muted text-sm">Kategori Keahlian</span><span className="font-semibold text-piks-black text-sm">{data.category}</span></div>
          <div className="flex justify-between py-2.5 border-b border-piks-border"><span className="text-piks-muted text-sm">Status Keahlian</span><span className={`font-semibold text-sm ${data.verified ? "text-emerald-600" : "text-piks-red"}`}>{data.status}</span></div>
          {data.company_name && (
            <div className="flex justify-between py-2.5 border-b border-piks-border">
              <span className="text-piks-muted text-sm flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" /> Syarikat</span>
              <span className="font-semibold text-piks-black text-sm">{data.company_name}</span>
            </div>
          )}
          {data.position && <div className="flex justify-between py-2.5 border-b border-piks-border"><span className="text-piks-muted text-sm">Jawatan</span><span className="font-semibold text-piks-black text-sm">{data.position}</span></div>}
          <div className="flex justify-between py-2.5 border-b border-piks-border"><span className="text-piks-muted text-sm">Ahli Sejak</span><span className="font-semibold text-piks-black text-sm">{formatDate(data.member_since)}</span></div>
          <div className="flex justify-between py-2.5"><span className="text-piks-muted text-sm">Sah Sehingga</span><span className="font-semibold text-piks-black text-sm">{formatDate(data.valid_until)}</span></div>
        </div>
      </motion.div>
    </div>
  );
}
